import { useMemo, useState } from 'react';
import { useDebouncedValue } from '@/hooks/state/use-debounced-value';
import { useAttributeNameSearch } from '@/services/db/attribute/queries';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import type { AttributeNamePickerProps } from '../components/attribute-row/attribute-row';
import type { ConditionControlSize } from '../components/operator-value/types';
import { attributeNameOptions } from './attribute-name-options';
import {
  AttributeNameSelect,
  type AttributeNameSearchState,
  type AttributeNameSelectProps,
} from './attribute-name-select';

/**
 * The picker for a profile attribute's name, shared by every surface that
 * filters on profile attributes.
 *
 * Shared because the names come from one place — the workspace's profile
 * attributes — whatever is being filtered. A surface with its own name source
 * (a collection's columns, say) supplies its own picker instead; this is only
 * for the profile-attribute ones.
 */

/** The picker's options, the term it is being searched by, and its state. */
export type ProfileAttributeNames = AttributeNameSearchState;

/**
 * The attribute-name options: the environment's bounded sent-attributes list
 * as the local seed, plus debounced server search. Searched names accumulate
 * so a picked search result keeps its option after the search clears.
 *
 * How the merged pool is narrowed and what else the list carries is the shared
 * rule in `attributeNameOptions`. Because the term externally controls the
 * select's input, the picker clears it on select and on close: a leftover term
 * is invisible in the reopened input yet still seeds the custom option, so
 * later keystrokes would append to it.
 *
 * The workspace is read from the URL unless the caller names it: a surface
 * reachable from a `/workspaces/last/...` route has no numeric id there, and
 * passes the one it resolved.
 */
export function useProfileAttributeNames(selectedField: string, workspaceIdOverride?: number): ProfileAttributeNames {
  const workspaceId = workspaceIdOverride ?? requireWorkspaceId();
  const environment = useEnvironmentOptional(workspaceId);
  const localAttributes = useMemo(() => environment.data?.environment?.sent_attributes ?? [], [environment.data]);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm] = useDebouncedValue(searchTerm, 300);
  const search = useAttributeNameSearch(workspaceId, debouncedTerm);

  const [searchedAttributes, setSearchedAttributes] = useState<readonly string[]>([]);
  const searchMatches = search.data;
  if (searchMatches != null) {
    const merged = [...new Set([...searchedAttributes, ...searchMatches])];
    if (merged.length !== searchedAttributes.length) {
      setSearchedAttributes(merged);
    }
  }

  const options = useMemo(
    () => attributeNameOptions([localAttributes, searchedAttributes], searchTerm, selectedField),
    [localAttributes, searchedAttributes, selectedField, searchTerm],
  );

  return { options, searchTerm, setSearchTerm, isLoading: search.isFetching };
}

type ProfileAttributeNamePickerProps = AttributeNamePickerProps & {
  /** The options and search state, from `useProfileAttributeNames`. */
  names: ProfileAttributeNames;
  /** What the closed select says while no attribute is named; "Profile attribute" by default. */
  placeholder?: string;
  /** Small by default, as in a filter builder. */
  size?: ConditionControlSize;
  /** Sizes the select, for a row that holds the name at a fixed width. */
  className?: string;
  /** Renders each entry, for a row that badges identifier and reserved attributes. */
  optionComponent?: AttributeNameSelectProps['optionComponent'];
};

/** The select a profile-attribute row names its attribute with. */
export function ProfileAttributeNamePicker(props: ProfileAttributeNamePickerProps) {
  return <AttributeNameSelect {...props} kindLabel="Profile attribute" testId="dryad-attribute-name" />;
}
